// src/components/common/compactListSx.ts

import type { SxProps, Theme } from '@mui/material';

/**
 * Compact list styles — applied via `sx` prop on React Admin's `<List>`.
 *
 * Overrides RA's internal padding, margins and alignment to produce
 * a tight, modern toolbar row (filters left, actions right) with
 * minimal vertical footprint.
 *
 * Usage:
 *   import { compactListSx } from '@/components/common/compactListSx';
 *   <List sx={compactListSx} ...>
 */
export const compactListSx: SxProps<Theme> = {
  // v3 Pattern A · desktop only: the List column fills the content area so the
  // table gets a real height to scroll inside (see CustomDataGridDesktop), the
  // pagination anchors at the bottom and the page itself never grows a long
  // scroll. Gated to ≥sm so mobile keeps its natural document scroll.
  '@media (min-width:600px)': {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    '& .RaList-main': {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    // .RaList-content is RA's Card wrapping the datagrid — fill the column.
    '& .RaList-content': {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
  },

  // ── Card wrapper ──────────────────────────────────────────────
  // v3: only the datagrid is a card. Neutralise RA's List Card (RaList-content)
  // so it doesn't draw a second frame ("recuadro") around/above the table.
  '& > .MuiCard-root, & .RaList-content': {
    marginBottom: '0 !important',
    overflow: 'hidden',
    border: 'none',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    borderRadius: 0,
  },

  // ── Main container (inside card) ─────────────────────────────
  '& .RaList-main': {
    paddingTop: '0 !important',
    paddingLeft: { xs: '4px !important', sm: '12px !important' },
    paddingRight: { xs: '4px !important', sm: '12px !important' },
    paddingBottom: '0 !important',
  },

  // ── Toolbar row (filters + actions) ──────────────────────────
  '& .RaList-actions': {
    minHeight: '0 !important',
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    marginBottom: '0 !important',
    borderBottom: 'none !important',
    // Hide the <span /> spacer that RA injects
    '& > span': { display: 'none' },
  },

  // ── ListToolbar wrapper ─────────────────────────────────────
  '& .RaListToolbar-root': {
    minHeight: '0 !important',
    padding: '0 !important',
  },

  // ── FilterForm (RA's <form> wrapper) ─────────────────────────
  '& .RaList-actions form': {
    margin: '0 !important',
    padding: '0 !important',
    minHeight: '0 !important',
    flex: '1 1 auto',
  },

  // ── FilterForm inner Box (has RA defaults: mb=1, align=flex-end)
  '& .RaList-actions form > .MuiBox-root': {
    alignItems: 'center !important',
    marginBottom: '0 !important',
    marginTop: '0 !important',
    minHeight: '0 !important',
  },

  // ── RA internal spacers (not needed — CollapsibleFilterPanel handles spacing)
  '& .RaFilterFormInput-spacer': {
    display: 'none !important',
  },
  '& .RaFilterForm-clearFix': {
    display: 'none !important',
  },
};
